<script>
import { Select } from 'ant-design-vue';
import RexSpinV3 from '@/rex-components/RexSpinV3.vue';
import RexTooltipV3 from '@/rex-components/RexTooltipV3.vue';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';
import { RexSelectOption } from '@/rex-components/rex.comp.config';
import {
  SELECT_FIELD_DEBOUNCE_DURATION_IN_MS,
  SELECT_FIELD_THROTTLE_DURATION_IN_MS,
} from '@/utils/constants';

export default {
  name: 'RexInfiniteScrollDropdown',
  emits: [
    'onChange',
    'onPopupScroll',
    'onDropdownVisibleChange',
    'onSearch',
    'onSelect',
    'onDeselect',
  ],
  components: {
    SelectComp: Select,
    RexSpinV3,
    RexTooltipV3,
    RexSelectOption,
  },
  props: {
    showSearch: {
      type: Boolean,
      default: false,
    },
    value: {
      type: [Array, String],
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    customStyle: {
      type: Object,
      default: () => ({ width: '20.75rem' }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    total: {
      type: Number,
      default: 0,
    },
    mode: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    searchKeyword: {
      type: String,
      default: '',
    },
    maxTagTextLength: {
      type: Number,
      default: null,
    },
    size: {
      type: String,
      default: 'default',
    },
    getPopupContainer: {
      type: Function,
      default: () => document.body,
    },
    stopInfiniteScroll: {
      type: Boolean,
      default: true,
    },
    heading: {
      type: String,
      default: '',
    },
    maxTagCount: {
      type: Number,
      default: null,
    },
    delimiter: {
      type: String,
      default: '-',
    },
  },
  computed: {
    getStyleObj() {
      return { textAlign: 'left', ...this.customStyle };
    },
  },
  methods: {
    scrollHandler(event) {
      const target = event.target;
      if (
        !this.loading &&
        target.scrollTop + target.offsetHeight >= target.scrollHeight - 10 &&
        (this.options.length < this.total || !this.stopInfiniteScroll)
      ) {
        this.throttlePopUp();
      }
    },
    getLabelColor(labelColor) {
      return {
        color: labelColor,
      };
    },
    throttlePopUp: throttle(async function () {
      await this.$emit('onPopupScroll');
    }, SELECT_FIELD_THROTTLE_DURATION_IN_MS),
    debouncedSearch: debounce(function (val) {
      this.$emit('onSearch', val);
    }, SELECT_FIELD_DEBOUNCE_DURATION_IN_MS),
  },
};
</script>

<template>
  <div class="rex-infi-scroll-dropdown-cont">
    <div
      class="rex-infi-scroll-dropdown-heading"
      v-if="heading && heading.length > 0"
    >
      {{ heading }} :
    </div>
    <SelectComp
      showSearch
      :mode="mode"
      :showArrow="true"
      :placeholder="placeholder"
      :value="value"
      :disabled="disabled"
      :filterOption="false"
      :dropdownStyle="{ textAlign: 'left' }"
      :style="getStyleObj"
      :maxTagTextLength="maxTagTextLength"
      :maxTagCount="maxTagCount"
      :size="size"
      :getPopupContainer="getPopupContainer"
      @popupScroll="(event) => scrollHandler(event)"
      @search="debouncedSearch"
      @dropdownVisibleChange="(open) => $emit('onDropdownVisibleChange', open)"
      @change="(value, option) => $emit('onChange', value, option)"
      @select="(value, option) => $emit('onSelect', value, option)"
      @deselect="(value, option) => $emit('onDeselect', value, option)"
    >
      <template #default>
        <RexSelectOption
          v-for="(obj, i) in options"
          :key="`${obj.key}${delimiter}${i}`"
          :value="obj.label"
          :disabled="obj.disabled"
          :metaProperty="obj"
        >
          <RexTooltipV3
            :title="obj.label"
            placement="right"
            class="rex-infi-scroll-dropdown-opt-tooltip"
          >
            <div
              class="rex-infi-scroll-dropdown-opt-tooltip-text"
              :style="obj?.color && getLabelColor(obj.color)"
            >
              {{ obj.label }}
            </div>
          </RexTooltipV3>
        </RexSelectOption>
        <RexSelectOption
          :disabled="true"
          v-if="loading"
          key="loading"
          :value="searchKeyword"
        >
          <div class="rex-infi-scroll-loading">
            <RexSpinV3 v-if="loading" size="small" />
          </div>
        </RexSelectOption>
      </template>
    </SelectComp>
  </div>
</template>

<style lang="scss" scoped>
.rex-infi-scroll-dropdown-cont {
  @include flex-center-justify(flex-start);
}
.rex-infi-scroll-dropdown-heading {
  @include heading-text;
  margin-right: 0.5625em;
}
.rex-infi-scroll-dropdown-opt-tooltip {
  overflow: hidden;
  white-space: nowrap;
}
.rex-infi-scroll-dropdown-opt-tooltip-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.rex-infi-scroll-loading {
  @include flex-center;
  height: 100%;
  width: 100%;
}
</style>
